<template>
  <div class="cashier-layout">
    <!-- 顶部蓝色栏：收银台(店铺名称) + 收银员下拉（克隆云贝截图3全局布局） -->
    <div class="cl-top">
      <div class="cl-title" title="点击店铺名称可切换门店">
        收银台<span v-if="storeName" class="cl-store" @click="openStoreModal">(店铺名称：{{ storeName }})</span>
      </div>
      <a-dropdown>
        <div class="cl-user">
          <a-avatar :size="28" :src="userInfo.avatar" icon="user" />
          <span class="cl-username">{{ userName }}</span>
          <a-icon type="down" class="cl-user-arrow" />
        </div>
        <a-menu slot="overlay">
          <a-menu-item key="profile" @click="profileVisible = true">
            <a-icon type="user" />个人信息
          </a-menu-item>
          <a-menu-item key="logout" @click="handleLogout">
            <a-icon type="poweroff" />退出登录
          </a-menu-item>
        </a-menu>
      </a-dropdown>
    </div>
    <div class="cl-body">
      <!-- 左侧深色窄边栏：收银/堂食/订单 三图标菜单 + 底部全屏模式 + 实时时间 -->
      <div class="cl-aside">
        <div class="cl-menu">
          <div v-for="m in menus" :key="m.key" class="cl-menu-item"
               :class="{ active: isActive(m) }" @click="go(m)">
            <a-icon :type="m.icon" class="cl-menu-icon" />
            <span class="cl-menu-label">{{ m.label }}</span>
          </div>
        </div>
        <div class="cl-aside-bottom">
          <div class="cl-fullscreen" :title="isFullscreen ? '退出全屏' : '全屏模式'" @click="toggleFullscreen">
            <a-icon :type="isFullscreen ? 'fullscreen-exit' : 'fullscreen'" class="cl-menu-icon" />
            <span class="cl-menu-label">全屏模式</span>
          </div>
          <div class="cl-clock">
            <div>{{ clockDate }}</div>
            <div class="cl-clock-time">{{ clockTime }}</div>
          </div>
        </div>
      </div>
      <!-- 三视图子路由：fastfood(收银)/table(堂食)/order(订单) -->
      <div class="cl-content">
        <router-view v-if="storeId" :store-id="storeId" :store-name="storeName" />
      </div>
    </div>
    <!-- 门店切换弹窗（照一期：未绑门店账号兜底 + 顶栏门店名点击切换） -->
    <a-modal title="选择门店" :visible="storeModalVisible" :footer="null" :maskClosable="false" @cancel="storeModalVisible = false">
      <div class="store-pick" v-for="s in storeOptions" :key="s.id" @click="pickStore(s)">
        <span class="store-pick-name">{{ s.name }}</span>
        <a-tag v-if="s.isMain === '1'" color="blue">总店</a-tag>
        <a-tag :color="s.status === '1' ? 'green' : 'red'">{{ s.status === '1' ? '营业' : '停用' }}</a-tag>
      </div>
      <div v-if="storeOptions.length === 0" class="store-pick-empty">当前租户尚未创建门店，请先在管理后台创建门店</div>
    </a-modal>
    <!-- 个人信息弹窗 -->
    <a-modal title="个人信息" :visible="profileVisible" :footer="null" @cancel="profileVisible = false">
      <div class="profile-row">
        <a-avatar :size="48" :src="userInfo.avatar" icon="user" />
        <div class="profile-text">
          <div class="profile-name">{{ userName || '-' }}</div>
          <div class="profile-login">账号：{{ userLoginName || '-' }}</div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import { USER_INFO, ACCESS_TOKEN } from '@/store/mutation-types'
import { getAction } from '@/api/manage'
import { storeMy, storeList, orderList, takeoutList } from '@/api/plugin/dining'

/**
 * 收银台骨架页（/cashier/dining，Task 6.1 克隆云贝全局布局）：
 * 顶蓝栏（收银台(店铺名称)+收银员下拉：个人信息/退出登录）+ 左深色窄边栏（收银/堂食/订单
 * 三图标菜单+全屏模式+实时时间）+ 三视图子路由（fastfood/table/order）。
 * 门店上下文解析（query.storeId → sessionStorage('diningStore') → storeMy → 门店列表兜底）。
 * Task 7.5 语音播报：15s 轮询订单列表（页面隐藏暂停，照一期模式）→ speechSynthesis
 * 播报取餐语音（fast 设置 phoneticWritingStart/End 模板）与新单提醒；TTS 不可用降级提示音+弹窗。
 */
export default {
  name: 'CashierLayout',
  data() {
    return {
      userInfo: {},
      storeName: '',
      storeId: null,
      storeOptions: [],
      storeModalVisible: false,
      profileVisible: false,
      isFullscreen: false,
      now: new Date(),
      clockTimer: null,
      fastSet: {},          // fast 设置（取餐语音前/后段模板）
      voiceKnown: {},       // orderId → { callNo, receiptAt } 播报基线（首帧静默，之后差量播报）
      voiceReady: false,    // 首次轮询仅建基线不播报
      takeoutKnown: {},     // Task 10.2：外卖 orderId → state 播报基线（state='2' 新增播外卖新单）
      takeoutReady: false,  // 外卖轮询首次仅建基线不播报（独立于堂食/快餐基线）
      voiceTimer: null,
      voiceLast: {},        // 播报去重 key → 时间戳（防结算即时播报与轮询差量重复）
      menus: [
        { key: 'fastfood', label: '收银', icon: 'money-collect', path: '/cashier/dining/fastfood' },
        { key: 'table', label: '堂食', icon: 'table', path: '/cashier/dining/table' },
        { key: 'order', label: '订单', icon: 'apps', path: '/cashier/dining/order' }
      ]
    }
  },
  computed: {
    userName() {
      return (this.userInfo && this.userInfo.username) || ''
    },
    userLoginName() {
      return (this.userInfo && (this.userInfo.loginName || this.userInfo.username)) || ''
    },
    clockDate() {
      return this.formatClock('yyyy-MM-dd')
    },
    clockTime() {
      return this.formatClock('HH:mm:ss')
    }
  },
  created() {
    const info = Vue.ls.get(USER_INFO)
    this.userInfo = info || {}
    this.resolveStore()
  },
  mounted() {
    this.clockTimer = setInterval(() => { this.now = new Date() }, 1000)
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
    // 收银视图结算成功即时取餐播报（7.2）：事件总线触发，与本轮询共用同一播报/去重链
    this.$root.$on('dining-call-speech', this.onCallSpeech)
    document.addEventListener('visibilitychange', this.onVoiceVisible)
  },
  beforeDestroy() {
    if (this.clockTimer) {
      clearInterval(this.clockTimer)
      this.clockTimer = null
    }
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
    this.$root.$off('dining-call-speech', this.onCallSpeech)
    document.removeEventListener('visibilitychange', this.onVoiceVisible)
    this.stopVoicePolling()
  },
  methods: {
    formatClock(pattern) {
      const d = this.now
      const pad = n => (n < 10 ? '0' + n : '' + n)
      if (pattern === 'yyyy-MM-dd') {
        return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
      }
      return pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds())
    },
    isActive(m) {
      return this.$route.path === m.path || this.$route.path.indexOf(m.path + '/') === 0
    },
    go(m) {
      if (this.$route.path !== m.path) {
        this.$router.push(m.path)
      }
    },
    toggleFullscreen() {
      if (document.fullscreenElement) {
        document.exitFullscreen()
      } else {
        document.documentElement.requestFullscreen()
      }
    },
    onFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement
    },
    // 门店上下文：query.storeId（门店列表"门店管理"带参打开）→ sessionStorage → storeMy → 列表兜底
    resolveStore() {
      const queryStoreId = Number(this.$route.query.storeId) || null
      let store = null
      try {
        store = JSON.parse(sessionStorage.getItem('diningStore') || 'null')
      } catch (e) {
        store = null
      }
      if (queryStoreId && (!store || store.id !== queryStoreId)) {
        this.resolveStoreById(queryStoreId)
        return
      }
      if (store && store.id) {
        this.applyStore(store)
        return
      }
      storeMy().then(res => {
        if (res.code === 200 && res.data && res.data.id) {
          sessionStorage.setItem('diningStore', JSON.stringify(res.data))
          this.applyStore(res.data)
        } else {
          this.fallbackStoreList()
        }
      }).catch(() => {
        this.fallbackStoreList()
      })
    },
    // 按 id 从门店列表定位（window.open 带 storeId 的门店管理入口）
    resolveStoreById(storeId) {
      storeList({ pageSize: 100 }).then(res => {
        const rows = (res.code === 200 && res.data && res.data.rows) || []
        const hit = rows.find(s => s.id === storeId)
        if (hit) {
          this.applyStoreAndCache(hit)
        } else {
          this.fallbackStoreList()
        }
      }).catch(() => {
        this.fallbackStoreList()
      })
    },
    // 门店列表兜底：单店自动进入，多店弹选择，无店提示
    fallbackStoreList() {
      storeList({ pageSize: 100 }).then(res => {
        const rows = (res.code === 200 && res.data && res.data.rows) || []
        if (rows.length === 1) {
          this.applyStoreAndCache(rows[0])
        } else if (rows.length > 1) {
          this.storeOptions = rows
          this.storeModalVisible = true
        } else {
          this.$message.error('当前租户尚未创建门店，请先在管理后台创建门店')
        }
      }).catch(() => {
        this.$message.error('获取门店信息失败，请重新登录')
        this.$router.push('/cashier/login')
      })
    },
    applyStoreAndCache(store) {
      sessionStorage.setItem('diningStore', JSON.stringify(store))
      this.applyStore(store)
    },
    // 顶栏门店名点击切换：重新拉候选列表（保证新建门店及时可见）
    openStoreModal() {
      storeList({ pageSize: 100 }).then(res => {
        if (res.code === 200 && res.data && res.data.rows) {
          this.storeOptions = res.data.rows
        }
      })
      this.storeModalVisible = true
    },
    pickStore(s) {
      this.storeModalVisible = false
      if (s && s.id && s.id !== this.storeId) {
        this.applyStoreAndCache(s)
      }
    },
    applyStore(store) {
      this.storeId = store.id
      this.storeName = store.name || ''
      this.loadFastSet()
      this.startVoicePolling()
    },
    // ============ 语音播报（Task 7.5） ============
    // fast 设置：取餐语音模板前段/后段（播报 = 前段 + 取餐号 + 后段）
    loadFastSet() {
      getAction('/api/plugin/dining/store-setting/fast', { storeId: this.storeId }).then(res => {
        if (res.code === 200 && res.data) {
          this.fastSet = res.data
        }
      }).catch(() => {})
    },
    startVoicePolling() {
      this.stopVoicePolling()
      this.voiceKnown = {}
      this.voiceReady = false
      this.takeoutKnown = {}
      this.takeoutReady = false
      this.voicePoll()
      this.takeoutVoicePoll()
      this.voiceTimer = setInterval(() => {
        if (!document.hidden) {
          this.voicePoll()
          this.takeoutVoicePoll()
        }
      }, 15000)
    },
    stopVoicePolling() {
      if (this.voiceTimer) {
        clearInterval(this.voiceTimer)
        this.voiceTimer = null
      }
    },
    // 页面恢复可见立即补一轮（隐藏期间定时器空转跳过，照一期模式）
    onVoiceVisible() {
      if (!document.hidden && this.storeId) {
        this.voicePoll()
        this.takeoutVoicePoll()
      }
    },
    // 轮询差量：新单（基线后新出现的订单）→ 新单提醒；快餐单 call_no 出现/变化 → 取餐语音
    voicePoll() {
      if (!this.storeId) return
      orderList({ storeId: this.storeId, currentPage: 1, pageSize: 50 }).then(res => {
        if (res.code !== 200 || !res.data || !res.data.rows) return
        const rows = res.data.rows || []
        const events = []
        rows.forEach(o => {
          const prev = this.voiceKnown[o.id]
          const callNo = o.call_no || null
          if (!prev) {
            if (this.voiceReady) {
              events.push({ call: !!(o.business_type === 'fastfood' && callNo), order: o })
            }
          } else if (o.business_type === 'fastfood' && callNo && callNo !== prev.callNo) {
            events.push({ call: true, order: o })
          }
          this.voiceKnown[o.id] = { callNo: callNo, receiptAt: o.receipt_at || null }
        })
        // 基线容量收敛（防长班次无限增长）
        const ids = Object.keys(this.voiceKnown)
        if (ids.length > 400) {
          ids.sort((a, b) => a - b).slice(0, ids.length - 400).forEach(k => { delete this.voiceKnown[k] })
        }
        this.voiceReady = true
        events.forEach(ev => {
          if (ev.call) {
            this.announceCall(ev.order.id, ev.order.call_no)
          } else {
            this.announceNew(ev.order.id)
          }
        })
      }).catch(() => {})
    },
    // 外卖轮询差量（Task 10.2）：外卖单进入 state='2' 已付款待接单 → 播"您有新的外卖订单"
    // （基线后新出现即 '2'，或已知单由非 '2' 推进到 '2'；与堂食/快餐播报共用 voiceDedupe 去重）
    takeoutVoicePoll() {
      if (!this.storeId) return
      takeoutList({ storeId: this.storeId, currentPage: 1, pageSize: 50 }).then(res => {
        if (res.code !== 200 || !res.data || !res.data.rows) return
        const rows = res.data.rows || []
        rows.forEach(o => {
          const prev = this.takeoutKnown[o.id]
          const state = String(o.state)
          if ((this.takeoutReady && !prev && state === '2') ||
              (prev && prev !== '2' && state === '2')) {
            this.announceTakeoutNew(o.id)
          }
          this.takeoutKnown[o.id] = state
        })
        // 基线容量收敛（防长班次无限增长）
        const ids = Object.keys(this.takeoutKnown)
        if (ids.length > 400) {
          ids.sort((a, b) => a - b).slice(0, ids.length - 400).forEach(k => { delete this.takeoutKnown[k] })
        }
        this.takeoutReady = true
      }).catch(() => {})
    },
    announceTakeoutNew(orderId) {
      if (!this.voiceDedupe('takeout-new:' + orderId)) return
      this.speakText('您有新的外卖订单')
    },
    // 收银视图结算成功即时播报（事件总线；与轮询共用去重键防双播）
    onCallSpeech(payload) {
      if (payload && payload.orderId && payload.callNo) {
        this.announceCall(payload.orderId, payload.callNo)
      }
    },
    announceCall(orderId, callNo) {
      if (!callNo || !this.voiceDedupe('call:' + orderId + ':' + callNo)) return
      const start = this.fastSet.phoneticWritingStart || '请'
      const end = this.fastSet.phoneticWritingEnd || '号到服务台取餐'
      this.speakText(start + callNo + end)
    },
    announceNew(orderId) {
      if (!this.voiceDedupe('new:' + orderId)) return
      this.speakText('您有新的订单')
    },
    // 一分钟内同键只播一次（结算即时播报 ↔ 轮询差量识别同一事件时去重）
    voiceDedupe(key) {
      const now = Date.now()
      if (this.voiceLast[key] && now - this.voiceLast[key] < 60000) return false
      this.voiceLast[key] = now
      const keys = Object.keys(this.voiceLast)
      if (keys.length > 200) {
        keys.sort((a, b) => this.voiceLast[a] - this.voiceLast[b])
          .slice(0, keys.length - 200).forEach(k => { delete this.voiceLast[k] })
      }
      return true
    },
    // TTS 播报；浏览器不支持/异常时降级：提示音 + 通知弹窗标记
    speakText(text) {
      const synth = window.speechSynthesis
      if (!synth || typeof synth.speak !== 'function' ||
          typeof window.SpeechSynthesisUtterance !== 'function') {
        this.voiceFallback(text)
        return
      }
      try {
        const utter = new window.SpeechSynthesisUtterance(text)
        utter.lang = 'zh-CN'
        utter.rate = 1
        synth.cancel()
        synth.speak(utter)
      } catch (e) {
        this.voiceFallback(text)
      }
    },
    voiceFallback(text) {
      this.playBeep()
      this.$notification.warning({ message: '语音提醒', description: text, duration: 6 })
    },
    // WebAudio 短提示音（无外部资源；播放完关闭上下文防泄漏）
    playBeep() {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext
        if (!Ctx) return
        const ctx = new Ctx()
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.value = 880
        gain.gain.setValueAtTime(0.35, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5)
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start()
        osc.stop(ctx.currentTime + 0.5)
        osc.onended = () => { ctx.close() }
      } catch (e) {}
    },
    handleLogout() {
      this.$store.dispatch('Logout').then(() => {
        Vue.ls.remove(ACCESS_TOKEN)
        sessionStorage.removeItem('diningStore')
        this.$router.push('/cashier/login')
      })
    }
  }
}
</script>

<style scoped>
.cashier-layout {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f5f6f8;
}
.cl-top {
  height: 50px;
  flex-shrink: 0;
  background: #1e3c66;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
}
.cl-title {
  font-size: 16px;
  font-weight: 600;
}
.cl-store {
  font-size: 13px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.82);
  margin-left: 4px;
  cursor: pointer;
}
.cl-store:hover {
  color: #fff;
}
.cl-user {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
}
.cl-user:hover {
  background: rgba(255, 255, 255, 0.12);
}
.cl-username {
  margin: 0 6px 0 8px;
  font-size: 13px;
}
.cl-user-arrow {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
}
.cl-body {
  flex: 1;
  display: flex;
  min-height: 0;
}
.cl-aside {
  width: 72px;
  flex-shrink: 0;
  background: #1f2d3d;
  display: flex;
  flex-direction: column;
  color: rgba(255, 255, 255, 0.72);
}
.cl-menu {
  flex: 1;
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.cl-menu-item,
.cl-fullscreen {
  width: 60px;
  padding: 9px 0 7px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s;
}
.cl-menu-item:hover,
.cl-fullscreen:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
.cl-menu-item.active {
  background: #1890ff;
  color: #fff;
}
.cl-menu-icon {
  font-size: 20px;
  line-height: 1;
}
.cl-menu-label {
  font-size: 11px;
  margin-top: 5px;
}
.cl-aside-bottom {
  flex-shrink: 0;
  padding: 10px 0 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.cl-fullscreen {
  width: 60px;
  padding: 7px 0 6px;
}
.cl-clock {
  margin-top: 10px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  line-height: 1.5;
  word-break: break-all;
  padding: 0 3px;
}
.cl-clock-time {
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 0.5px;
}
.cl-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.store-pick {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  margin-bottom: 8px;
  cursor: pointer;
}
.store-pick:hover {
  border-color: #1890ff;
  background: #e6f7ff;
}
.store-pick-name {
  flex: 1;
  font-size: 14px;
}
.store-pick-empty {
  text-align: center;
  color: #999;
  padding: 16px 0;
}
.profile-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 6px 2px;
}
.profile-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}
.profile-login {
  font-size: 13px;
  color: #999;
  margin-top: 4px;
}
</style>
